import React, { useState } from 'react';
import { Flex, Gutter, Title, BodyText, GridBag } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

import './LongDescription.scss';

interface LongDescriptionProps {
  title: string;
  description: string | undefined;
  descriptionTranslations: { localeId: number; translation: string }[] | undefined;
  locale: LocaleResponse;
  handleDescriptionChange: (description: string) => void;
  handleDescriptionTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  errors: Errors['description'];
}

function LongDescription(props: LongDescriptionProps) {
  const { title, description, descriptionTranslations, locale, handleDescriptionChange, handleDescriptionTranslationsChange, errors } = props;
  const { t } = useTranslation('productscreen');

  const [descriptionReferenceInputOpen, setDescriptionReferenceInputOpen] = useState<boolean>(false);

  const handleDescriptionTranslationChange = (descriptionTranslation: string) => {
    const index = (descriptionTranslations || []).findIndex(translation => translation.localeId === locale.id);
    if (index !== -1 && descriptionTranslations) {
      const updatedTranslation = { ...descriptionTranslations[index], translation: descriptionTranslation };
      const updatedTranslations = updateEntityByIndex(descriptionTranslations, index, updatedTranslation);
      handleDescriptionTranslationsChange(updatedTranslations);
    }
  };

  const currentTranslation = descriptionTranslations?.find(translation => translation.localeId === locale.id)?.translation;

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('long_description_section.info')}</BodyText>
      <Gutter />
      <GridBag>
        <GridBag.Item md={7}>
          <EditableItem>
            <EditableItem.TextArea
              value={currentTranslation}
              edit={true}
              textAreaProps={{
                name: 'longDescriptionTranslation',
                onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => handleDescriptionTranslationChange(e.target.value),
                placeholder: t('long_description_section.description_translation_placeholder', { language: locale.name.split(' ')[0] }),
                label: t('long_description_section.description_translation_label'),
                errorText: errors?.descriptionTranslation,
                required: true,
                onKeyDown: () => undefined,
              }}
            />
            <Gutter size="sm" />
            <EditableItem.TextArea
              value={description}
              edit={descriptionReferenceInputOpen}
              isReference={true}
              textAreaProps={{
                name: 'longDescription',
                onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => {
                  handleDescriptionChange(e.target.value);
                  setDescriptionReferenceInputOpen(false);
                },
                placeholder: t('long_description_section.description_reference_placeholder'),
                label: t('long_description_section.description_reference_label'),
                errorText: errors?.description,
                required: true,
                onKeyDown: () => undefined,
              }}
              editIconProps={{
                'data-testid': `long-description-edit`,
                onClick: () => setDescriptionReferenceInputOpen(true),
              }}
            />
          </EditableItem>
        </GridBag.Item>
      </GridBag>
    </Flex>
  );
}

export default React.memo(LongDescription);
